// Exact selections on a plate's picture (full size, 1920×1080), for the
// people editor: a magic wand, edges snapped to the picture, grow / shrink.
// A selection is a Uint8Array (0 or 255 per pixel), the picture an ImageData.

/** Pixels like the one at (x, y) — within `tolerance` (0…100) of its colour — touching it (contiguous) or anywhere. */
export function magicWand(img, x, y, tolerance = 20, contiguous = true) {
  const { width: w, height: h, data } = img;
  const out = new Uint8Array(w * h);
  const i0 = (Math.round(y) * w + Math.round(x));
  if (i0 < 0 || i0 >= w * h) return out;
  const r0 = data[i0 * 4]; const g0 = data[i0 * 4 + 1]; const b0 = data[i0 * 4 + 2];
  const limit = (tolerance / 100) * 441;
  const lim2 = limit * limit;
  const near = (i) => {
    const dr = data[i * 4] - r0; const dg = data[i * 4 + 1] - g0; const db = data[i * 4 + 2] - b0;
    return dr * dr + dg * dg + db * db <= lim2;
  };
  if (!contiguous) {
    for (let i = 0; i < w * h; i++) if (near(i)) out[i] = 255;
    return out;
  }
  const stack = new Int32Array(w * h); let top = 0;
  stack[top++] = i0; out[i0] = 255;
  while (top) {
    const i = stack[--top]; const px = i % w;
    if (px > 0 && !out[i - 1] && near(i - 1)) { out[i - 1] = 255; stack[top++] = i - 1; }
    if (px < w - 1 && !out[i + 1] && near(i + 1)) { out[i + 1] = 255; stack[top++] = i + 1; }
    if (i >= w && !out[i - w] && near(i - w)) { out[i - w] = 255; stack[top++] = i - w; }
    if (i < w * (h - 1) && !out[i + w] && near(i + w)) { out[i + w] = 255; stack[top++] = i + w; }
  }
  // Pin-holes (anti-aliased specks inside a flat shape) closed.
  return closeHoles(out, w, h, 2);
}

/** A selection's bounding box (with a margin), or null when empty. */
function boxOf(mask, w, h, margin) {
  let x0 = w; let y0 = h; let x1 = -1; let y1 = -1;
  for (let i = 0; i < mask.length; i++) {
    if (!mask[i]) continue;
    const x = i % w; const y = (i - x) / w;
    if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  if (x1 < 0) return null;
  return { x0: Math.max(0, x0 - margin), y0: Math.max(0, y0 - margin), x1: Math.min(w - 1, x1 + margin), y1: Math.min(h - 1, y1 + margin) };
}

/** The largest (or smallest, with `min`) value within r pixels, in a square: grows (max) or shrinks (min) a selection. */
function spread(mask, w, h, r, min = false) {
  if (r <= 0) return Uint8Array.from(mask);
  const b = boxOf(min ? mask : mask, w, h, r + 1);
  if (!b) return new Uint8Array(w * h);
  const pick = min ? Math.min : Math.max;
  const tmp = Uint8Array.from(mask); const out = Uint8Array.from(mask);
  for (let y = b.y0; y <= b.y1; y++) {
    for (let x = b.x0; x <= b.x1; x++) {
      let v = min ? 255 : 0;
      for (let k = -r; k <= r; k++) { const xx = x + k; v = pick(v, xx < 0 || xx >= w ? (min ? 0 : 0) : mask[y * w + xx]); }
      tmp[y * w + x] = v;
    }
  }
  for (let y = b.y0; y <= b.y1; y++) {
    for (let x = b.x0; x <= b.x1; x++) {
      let v = min ? 255 : 0;
      for (let k = -r; k <= r; k++) { const yy = y + k; v = pick(v, yy < 0 || yy >= h ? 0 : tmp[yy * w + x]); }
      out[y * w + x] = v;
    }
  }
  return out;
}
export const grow = (mask, w, h, r) => spread(mask, w, h, r, false);
export const shrink = (mask, w, h, r) => spread(mask, w, h, r, true);
/** Small holes and gaps closed (grow, then shrink back). */
export const closeHoles = (mask, w, h, r) => shrink(grow(mask, w, h, r), w, h, r);

/**
 * Edges snapped to the picture: within `band` pixels of the selection's
 * edge, each pixel goes with whichever it looks more like — the selection's
 * own colours near it, or the colours just outside it (local averages over a
 * window). A rough brush or polygon becomes the object's exact outline.
 */
export function snapEdges(img, mask, band = 8) {
  const { width: w, height: h, data } = img;
  const b = boxOf(mask, w, h, band * 4);
  if (!b) return Uint8Array.from(mask);
  const inner = shrink(mask, w, h, band);
  const outer = grow(mask, w, h, band);
  const bw = b.x1 - b.x0 + 1; const bh = b.y1 - b.y0 + 1;
  // Integral images (over the box) of colour × "sure inside" and × "sure outside", and their counts.
  const W1 = bw + 1;
  const sums = [new Float64Array(W1 * (bh + 1) * 4), new Float64Array(W1 * (bh + 1) * 4)];
  for (let y = 0; y < bh; y++) {
    const row = [[0, 0, 0, 0], [0, 0, 0, 0]];
    for (let x = 0; x < bw; x++) {
      const i = (b.y0 + y) * w + b.x0 + x;
      const sides = [inner[i] ? 1 : 0, outer[i] ? 0 : 1];
      for (let s = 0; s < 2; s++) {
        if (sides[s]) { row[s][0] += data[i * 4]; row[s][1] += data[i * 4 + 1]; row[s][2] += data[i * 4 + 2]; row[s][3] += 1; }
        const k = ((y + 1) * W1 + x + 1) * 4; const up = (y * W1 + x + 1) * 4;
        for (let c = 0; c < 4; c++) sums[s][k + c] = sums[s][up + c] + row[s][c];
      }
    }
  }
  const win = Math.max(10, band * 3);
  const mean = (s, x, y) => {
    const xa = Math.max(0, x - win); const xb = Math.min(bw, x + win + 1); const ya = Math.max(0, y - win); const yb = Math.min(bh, y + win + 1);
    const A = (ya * W1 + xa) * 4; const B = (ya * W1 + xb) * 4; const C = (yb * W1 + xa) * 4; const D = (yb * W1 + xb) * 4;
    const n = sums[s][D + 3] - sums[s][B + 3] - sums[s][C + 3] + sums[s][A + 3];
    if (n < 4) return null;
    return [0, 1, 2].map((c) => (sums[s][D + c] - sums[s][B + c] - sums[s][C + c] + sums[s][A + c]) / n);
  };
  const out = Uint8Array.from(mask);
  for (let y = 0; y < bh; y++) {
    for (let x = 0; x < bw; x++) {
      const i = (b.y0 + y) * w + b.x0 + x;
      if (inner[i] || !outer[i]) continue; // sure inside / sure outside: as they are
      const mi = mean(0, x, y); const mo = mean(1, x, y);
      if (!mi || !mo) continue;
      const p = [data[i * 4], data[i * 4 + 1], data[i * 4 + 2]];
      const d = (m) => (p[0] - m[0]) ** 2 + (p[1] - m[1]) ** 2 + (p[2] - m[2]) ** 2;
      out[i] = d(mi) <= d(mo) ? 255 : 0;
    }
  }
  return closeHoles(out, w, h, 1);
}

/** How many pixels are selected. */
export const countOf = (mask) => { let n = 0; for (let i = 0; i < mask.length; i++) if (mask[i]) n++; return n; };
